// src/components/ProjectDetails.jsx
import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import "./styles/ProjectDetails.css";
import "../components/styles/FileActionModal.css";
import { Link, useNavigate } from "react-router-dom";
import VariableContext from "../contexts/VariableContext";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../providers/AuthProvider";
import CommonButton from "../elments/CommonButton";
import FileActionModal from "../components/FileActionModal";

// ─── Icon SVGs ───────────────────────────────────────────────────────────────

const commonIconProps = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  width: 18,
  height: 18,
};

const IconReplace = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
    <path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
  </svg>
);
const IconUpload = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);
const IconFile = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);
const IconWarning = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...commonIconProps}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const IconInfo = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...commonIconProps}>
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" {...commonIconProps}>
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconDatabase = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
  </svg>
);
const IconUsers = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const IconChevronDown = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const IconSearch = () => (
  <svg viewBox="0 0 24 24" {...commonIconProps}>
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

// Feature icons map
const FeatureIcons = {
  scripting: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
    </svg>
  ),
  datacheck: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  datatables: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/>
    </svg>
  ),
  banner: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/>
    </svg>
  ),
  weighting: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <circle cx="12" cy="5" r="3"/><path d="M6.5 8a7 7 0 0 0 0 8"/><path d="M17.5 8a7 7 0 0 1 0 8"/>
      <line x1="12" y1="19" x2="12" y2="22"/>
    </svg>
  ),
  dataanalysis: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  dashboard: () => (
    <svg viewBox="0 0 24 24" {...commonIconProps}>
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
};

// ─── Reusable DropZone ────────────────────────────────────────────────────────
const DropZone = ({ accept, onFileSelect, selectedFile, hint }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = React.useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFileSelect(file);
  };

  return (
    <div
      className={`fam-dropzone${dragging ? " fam-dropzone--dragging" : ""}${selectedFile ? " fam-dropzone--selected" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => e.target.files[0] && onFileSelect(e.target.files[0])}
        style={{ display: "none" }}
      />
      <div className="fam-dropzone-icon">{selectedFile ? "✅" : "📂"}</div>
      {selectedFile ? (
        <>
          <div className="fam-dropzone-text">File selected</div>
          <div className="fam-dropzone-filename">✓ {selectedFile.name}</div>
        </>
      ) : (
        <>
          <div className="fam-dropzone-text">Drop file here or click to browse</div>
          <div className="fam-dropzone-hint">{hint}</div>
        </>
      )}
    </div>
  );
};

// ─── Progress Bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ value, color = "purple", label }) => (
  <div className="fam-progress">
    <div className="fam-progress-track">
      <div
        className={`fam-progress-fill${color === "green" ? " fam-progress-fill--green" : color === "orange" ? " fam-progress-fill--orange" : ""}`}
        style={{ width: `${value}%` }}
      />
    </div>
    {label && <div className="fam-progress-text">{label}</div>}
  </div>
);

// ─── Helper: get initials from email ─────────────────────────────────────────
const getInitials = (email = "") => {
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ProjectDetails = ({ project, projectid, projectOwner }) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const {
    banners,
    savedWeightings,
    setSdataGroup,
    dataGroup,
    projectId,
    setdataGroup,
    setProjectId,
    setPID,
    setPName
  } = useContext(VariableContext);

  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [users, setUsers]                         = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [selectedUsers, setSelectedUsers]         = useState([]);
  const [showUsers, setShowUsers]                 = useState(false);
  const [isUpdating, setIsUpdating]               = useState(false);
  const [updateResult, setUpdateResult]           = useState(null);
  const [localQnrFilename, setLocalQnrFilename]   = useState(null);
  const [allowedFeatures, setAllowedFeatures]     = useState([]);
  const [searchTerm, setSearchTerm]               = useState(""); // New state for search

  // ── Modal flags ────────────────────────────────────────────────────────────
  const [showReplaceModal, setShowReplaceModal]   = useState(false);
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [showQnrModal, setShowQnrModal]           = useState(false);

  // ── Replace SPSS state ────────────────────────────────────────────────────
  const [selectedReplaceFile, setSelectedReplaceFile]   = useState(null);
  const [replaceFileUploading, setReplaceFileUploading] = useState(false);
  const [replaceProgress, setReplaceProgress]           = useState(0);

  // ── Re-upload state ───────────────────────────────────────────────────────
  const [reuploadFile, setReuploadFile]               = useState(null);
  const [reuploadMode_type, setReuploadModeType]      = useState("truncate");
  const [reuploadLoading, setReuploadLoading]         = useState(false);
  const [reuploadProgress, setReuploadProgress]       = useState(0);

  // ── QNR state ─────────────────────────────────────────────────────────────
  const [selectedQnrFile, setSelectedQnrFile] = useState(null);
  const [qnrUploading, setQnrUploading]       = useState(false);
  const [qnrProgress, setQnrProgress]         = useState(0);

  // ─────────────────────────────────────────────────────────────────────────
  const memoizedProject = useMemo(() => project, [project]);
  const { client, project: proj, filename, uploaded_time, database_name } = memoizedProject || {};
  const qnrFilename = localQnrFilename || memoizedProject?.qnr_filename;

  const numberOfQuestions = useMemo(() => {
    if (!memoizedProject) return 0;
    return memoizedProject.qgroup_json
      ? Object.keys(memoizedProject.qgroup_json).length
      : memoizedProject.sqgroup_json
        ? Object.keys(memoizedProject.sqgroup_json).length
        : 0;
  }, [memoizedProject]);

  // ── Filtered users based on search term ──────────────────────────────────
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const searchLower = searchTerm.toLowerCase().trim();
    return users.filter(userItem => 
      userItem.displayName?.toLowerCase().includes(searchLower) ||
      userItem.mail?.toLowerCase().includes(searchLower) ||
      userItem.email?.toLowerCase().includes(searchLower)
    );
  }, [users, searchTerm]);

  // ── Features ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;
    if (organization?.features?.length > 0) {
      setAllowedFeatures(organization.features);
    } else {
      setAllowedFeatures(["scripting","datacheck","datatables","banner","weighting","dataanalysis","dashboardbuilder","crosstab"]);
    }
  }, [user]);

  // ── Modal close helpers ───────────────────────────────────────────────────
  const closeReplaceModal = useCallback(() => {
    setShowReplaceModal(false); setSelectedReplaceFile(null); setReplaceProgress(0);
  }, []);

  const closeReuploadModal = useCallback(() => {
    setShowReuploadModal(false); setReuploadFile(null); setReuploadProgress(0);
  }, []);

  const closeQnrModal = useCallback(() => {
    setShowQnrModal(false); setSelectedQnrFile(null); setQnrProgress(0);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleReplaceFile = useCallback(async () => {
    if (!selectedReplaceFile) { toast.warning("Please select a file first."); return; }
    setReplaceFileUploading(true);
    setReplaceProgress(10);
    const formData = new FormData();
    formData.append("file", selectedReplaceFile);
    formData.append("projectId", projectid);
    formData.append("client", JSON.stringify(client || {}));
    formData.append("project", JSON.stringify(proj || {}));
    formData.append("user", projectOwner);
    try {
      setReplaceProgress(50);
      const { data: result } = await apiService.upload("/save-data-to-sql", formData);
      setReplaceProgress(100);
      if (result.success) {
        toast.success("SPSS file replaced successfully!");
        setTimeout(() => { closeReplaceModal(); window.location.reload(); }, 600);
      } else {
        toast.error("Error replacing file: " + result.message);
        setReplaceProgress(0);
      }
    } catch (error) {
      toast.error("Error replacing file: " + error.message);
      setReplaceProgress(0);
    } finally {
      setReplaceFileUploading(false);
    }
  }, [selectedReplaceFile, projectid, client, proj, projectOwner, closeReplaceModal, toast]);

  const handleReuploadSpss = useCallback(async () => {
    if (!reuploadFile) { toast.warning("Please select an SPSS file first."); return; }
    setReuploadLoading(true);
    setReuploadProgress(10);
    const formData = new FormData();
    formData.append("file", reuploadFile);
    formData.append("project_id", projectid);
    formData.append("upload_mode", reuploadMode_type);
    const userEmail = user?.user?.email || user?.email || "";
    formData.append("user_email", userEmail);
    try {
      setReuploadProgress(50);
      const { data } = await apiService.upload("/save-data-to-sql", formData);
      setReuploadProgress(100);
      if (data.success) {
        toast.success(`Data ${reuploadMode_type === "truncate" ? "replaced" : "appended"} successfully!`);
        setTimeout(closeReuploadModal, 700);
      } else {
        toast.error("Error re-uploading: " + (data.message || "Unknown error"));
        setReuploadProgress(0);
      }
    } catch (error) {
      toast.error("Error re-uploading SPSS: " + error.message);
      setReuploadProgress(0);
    } finally {
      setReuploadLoading(false);
    }
  }, [reuploadFile, reuploadMode_type, user, projectid, closeReuploadModal, toast]);

  const handleUploadQnr = useCallback(async () => {
    if (!selectedQnrFile) { toast.warning("Please select a QNR file first."); return; }
    setQnrUploading(true);
    setQnrProgress(10);
    const formData = new FormData();
    formData.append("file", selectedQnrFile);
    formData.append("projectId", projectid);
    formData.append("client", JSON.stringify(client || {}));
    formData.append("project", JSON.stringify(proj || {}));
    try {
      setQnrProgress(50);
      const { data: result } = await apiService.upload("/upload-qnr", formData);
      setQnrProgress(100);
      if (result.success) {
        toast.success("QNR file uploaded successfully!");
        setLocalQnrFilename(selectedQnrFile.name);
        setTimeout(closeQnrModal, 700);
      } else {
        toast.error("Error uploading QNR: " + result.message);
        setQnrProgress(0);
      }
    } catch (error) {
      toast.error("Error uploading QNR: " + error.message);
      setQnrProgress(0);
    } finally {
      setQnrUploading(false);
    }
  }, [selectedQnrFile, projectid, client, proj, closeQnrModal, toast]);

  const handleUpdateFromScripting = useCallback(() => {
    if (!dataGroup) { toast.warning("Please load a project with SPSS data first."); return; }
    if (!qnrFilename) { toast.warning("Please upload a QNR file first."); return; }
    setIsUpdating(true);
    setUpdateResult(null);
    apiService.post(`/project/${projectId}/validate_and_update`, {
      data_group: dataGroup,
      qnr_filename: qnrFilename,
    })
      .then(({ data }) => {
        if (data.success) {
          setdataGroup(data.updated_data_group);
          setUpdateResult({ success: true, message: `Updated ${data.stats.changed_variables} variables` });
          toast.success(`Validation complete! ${data.stats.changed_variables} variables updated.`);
        } else {
          setUpdateResult({ success: false, message: data.error || "Update failed" });
          toast.error("Update failed: " + (data.error || "Unknown error"));
        }
      })
      .catch((error) => {
        setUpdateResult({ success: false, message: error.message });
        toast.error("Error during update: " + error.message);
      })
      .finally(() => setIsUpdating(false));
  }, [dataGroup, qnrFilename, projectId, setdataGroup, toast]);

  const fetchOfficeUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiService.get("/office-users");
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCheckboxChange = useCallback((email) => {
    setSelectedUsers((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  }, []);

  const handleShareProject = useCallback(async () => {
    if (selectedUsers.length === 0) return;
    try {
      await apiService.post("/share-project", {
        projectId: projectid,
        user_ids: selectedUsers,
        client_name: client?.name || "",
        project_name: proj?.name || "",
        user: projectOwner,
      });
      toast.success("Project shared successfully!");
      setShowUsers(false);
      setSelectedUsers([]);
      setSearchTerm(""); // Reset search when sharing is complete
    } catch {
      toast.error("Failed to share project.");
    }
  }, [selectedUsers, projectid, client, proj, projectOwner, toast]);

  const handleScriptingClick = useCallback((e) => {
    e.preventDefault();
    if (!memoizedProject) return;
    if (memoizedProject.sqgroup_json) {
      setSdataGroup(memoizedProject.sqgroup_json);
      setProjectId(projectid);
    }
    navigate("/surveybuilder");
  }, [memoizedProject, projectid, setSdataGroup, setProjectId, navigate]);

  const handleDelete = async () => {
    if (!projectid) { toast.warning("Please select a project to delete."); return; }
    if (!window.confirm("Are you sure you want to delete this project? This action cannot be undone.")) return;
    setLoading(true);
    try {
      const { data: result } = await apiService.delete(`/api/projects/${projectId}/delete`);
      if (result.success) {
        toast.success("Project deleted successfully!");
        window.location.reload();
      } else {
        toast.error(result.message || "Failed to delete project.");
      }
    } catch (err) {
      toast.error(err.message || "An error occurred while deleting the project.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (memoizedProject) {
      if (memoizedProject.qgroup_json)  setdataGroup(memoizedProject.qgroup_json);
      if (memoizedProject.sqgroup_json) setSdataGroup(memoizedProject.sqgroup_json);
      if (projectid) setProjectId(projectid);
    }
  }, [memoizedProject, projectid, setdataGroup, setSdataGroup, setProjectId]);

  if (!memoizedProject) {
    return (
      <div className="project-details-noselect">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
        </svg>
        Select a project to view its details
      </div>
    );
  }

  const features = user?.user?.organization?.features || user?.organization?.features || {};

  const featureList = [
    { key: "scripting",    label: "Scripting",      to: "/surveybuilder", onClick: handleScriptingClick },
    { key: "datacheck",    label: "DV",              to: "/datacheck" },
    { key: "datatables",   label: "Data Tables",     to: "/datatables" },
    { key: "banner",       label: "Banner",          to: "/banner" },
    { key: "weighting",    label: "Weighting",       to: "/weighting" },
    { key: "dataanalysis", label: "Data Analysis",   to: "/dataanalysis" },
    { key: "dashboard",    label: "Dashboard",       to: "/dashboardbuilder" },
  ];

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="project-details">

      {/* ── Header ── */}
      <div className="pd-header">
        <div className="pd-header-left">
          <div className="pd-avatar">{getInitials(projectOwner)}</div>
          <div>
            <div className="pd-header-title">{proj?.name || "Project details"}</div>
            <div className="pd-header-subtitle">
              {client?.name || "—"} · {projectOwner}
            </div>
          </div>
        </div>
        <div className="pd-header-actions">
          <button
            className="pd-btn pd-btn--share"
            onClick={() => { setShowUsers(!showUsers); if (!showUsers) fetchOfficeUsers(); }}
          >
            <IconUsers /> Share project
          </button>
          <button className="pd-btn pd-btn--danger" onClick={handleDelete} disabled={loading}>
            <IconTrash /> Delete
          </button>
        </div>
      </div>

      {/* ── Quick actions (moved up for visibility & interaction) ── */}
      <div className="pd-section-label">Features</div>
      <div className="pd-features pd-features--cards">
        {featureList.map(({ key, label, to, onClick }) => {
          const Icon = FeatureIcons[key];
          if (!features[key]) return null;
          return (
            <Link
              key={key}
              to={to}
              className="pd-feature-link"
              onClick={onClick}
            >
              {Icon && <Icon />} {label}
            </Link>
          );
        })}
      </div>

      {/* ── Share accordion (just below header) ── */}
      {showUsers && (
        <div className="pd-share-box" style={{ marginBottom: "18px" }}>
          <div
            className="pd-share-header"
            onClick={() => setShowUsers(!showUsers)}
          >
            <div className="pd-share-header-left">
              <IconUsers /> Share with team members
            </div>
            <svg
              className={`pd-share-chevron${showUsers ? " pd-share-chevron--open" : ""}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
          <div className="pd-share-body">
            <h4>Select users to share with</h4>
            
            {/* ── SEARCH BAR ── */}
            <div className="pd-search-container">
              <div className="pd-search-input-wrapper">
                <IconSearch />
                <input
                  type="text"
                  className="pd-search-input"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="pd-search-clear"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              {searchTerm && (
                <div className="pd-search-results-count">
                  {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""} found
                </div>
              )}
            </div>

            <div className="pd-user-list">
              {loading ? (
                <div style={{ fontSize: 13, color: "#9ca3af", padding: "8px 0" }}>Loading users…</div>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((userItem, index) => (
                  <div key={index} className="pd-user-item">
                    <input
                      type="checkbox"
                      value={userItem.email}
                      checked={selectedUsers.includes(userItem.mail)}
                      onChange={() => handleCheckboxChange(userItem.mail)}
                    />
                    <div className="pd-user-item-info">
                      <span className="pd-user-item-name">{userItem.displayName}</span>
                      <span className="pd-user-item-email">{userItem.mail || userItem.email}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 13, color: "#9ca3af", padding: "16px 0", textAlign: "center" }}>
                  {searchTerm ? "No users match your search." : "No users found in this organization."}
                </div>
              )}
            </div>
            {selectedUsers.length > 0 && (
              <div className="pd-share-confirm">
                <button className="pd-btn pd-btn--share" onClick={handleShareProject}>
                  Confirm share ({selectedUsers.length})
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Project info ── */}
      <div className="pd-section-label">Project details</div>
      <div className="pd-info-table">

        <div className="pd-info-row">
          <span className="pd-info-label">Owner</span>
          <span className="pd-info-value">{projectOwner || "—"}</span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Client</span>
          <span className="pd-info-value">{client?.name || "—"}</span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Project name</span>
          <span className="pd-info-value">{proj?.name || "—"}</span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Database</span>
          <span className="pd-info-value mono">{database_name || "—"}</span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">File name</span>
          <span className="pd-info-value mono">{filename || "—"}</span>
          <div className="pd-info-actions">
            <button className="pd-btn pd-btn--action" onClick={() => setShowReplaceModal(true)}>
              <IconReplace /> Replace SPSS
            </button>
            <button className="pd-btn pd-btn--action" onClick={() => setShowReuploadModal(true)}>
              <IconUpload /> Re-upload
            </button>
          </div>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Upload time</span>
          <span className="pd-info-value">
            {uploaded_time ? new Date(uploaded_time).toLocaleString() : "—"}
          </span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Questions</span>
          <span className="pd-info-value">{numberOfQuestions}</span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Weighting</span>
          <span className="pd-info-value">
            <span className={`pd-status-chip ${savedWeightings ? "pd-chip-ok" : "pd-chip-na"}`}>
              {savedWeightings ? <><IconCheck /> Applied</> : "Not set"}
            </span>
          </span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">Banners</span>
          <span className="pd-info-value">
            <span className={`pd-status-chip ${banners?.length ? "pd-chip-ok" : "pd-chip-na"}`}>
              {banners?.length ? <><IconCheck /> {banners.length} banner{banners.length > 1 ? "s" : ""}</> : "Not set"}
            </span>
          </span>
        </div>

        <div className="pd-info-row">
          <span className="pd-info-label">QNR file</span>
          <span className="pd-info-value">
            <span className={`pd-qnr-badge ${qnrFilename ? "pd-qnr-badge--ok" : "pd-qnr-badge--missing"}`}>
              {qnrFilename ? <><IconCheck /> {qnrFilename}</> : <><IconWarning /> Not uploaded</>}
            </span>
          </span>
          <div className="pd-info-actions">
            <button className="pd-btn pd-btn--action" onClick={() => setShowQnrModal(true)}>
              <IconUpload /> {qnrFilename ? "Replace QNR" : "Upload QNR"}
            </button>
          </div>
        </div>

      </div>

      {/* ── Data Validation ── */}
      {dataGroup && (
        <>
          <hr className="pd-divider" />
          <div className="pd-section-label">Data validation</div>
          <div className="pd-validation-box">
            <div className="pd-validation-title">
              <IconCheck /> Validate &amp; update from QNR
            </div>
            <div className="pd-validation-desc">
              Validate SPSS data against the uploaded QNR file.
              {!qnrFilename && (
                <span className="pd-validation-warn"> Upload a QNR file first to enable this.</span>
              )}
            </div>
            <button
              className="pd-btn"
              onClick={handleUpdateFromScripting}
              disabled={isUpdating || !dataGroup || !qnrFilename}
              style={{
                background: !qnrFilename ? "#f3f4f6" : isUpdating ? "#f3f4f6" : "#f0fdf4",
                color: !qnrFilename ? "#9ca3af" : "#065f46",
                borderColor: !qnrFilename ? "#e5e7eb" : "#6ee7b7",
              }}
            >
              <IconCheck />
              {isUpdating ? "Validating…" : "Validate & update"}
            </button>
            {updateResult && (
              <div className={`pd-validation-result ${updateResult.success ? "pd-validation-result--ok" : "pd-validation-result--err"}`}>
                <strong>{updateResult.success ? "✓ Success:" : "✗ Error:"}</strong> {updateResult.message}
              </div>
            )}
          </div>
        </>
      )}

      <hr className="pd-divider" />

      {/* ── Modals ── */}

      {/* MODAL 1 — Replace SPSS */}
      <FileActionModal
        isOpen={showReplaceModal}
        onClose={closeReplaceModal}
        type="replace"
        title="Replace SPSS file"
        icon={<IconReplace />}
        iconClass="warning"
      >
        <div className="fam-alert fam-alert--warning">
          <span className="fam-alert-icon"><IconWarning /></span>
          <span>
            This will replace the current SPSS file. All existing configurations, banners,
            and weightings will be preserved.
          </span>
        </div>
        <p className="fam-desc">
          Upload a new <code>.sav</code> file to replace <strong>{filename || "the current file"}</strong>.
          Ensure your new file has the same variable structure.
        </p>
        <DropZone accept=".sav,.SAV" onFileSelect={setSelectedReplaceFile} selectedFile={selectedReplaceFile} hint=".sav / .SAV files only" />
        {replaceProgress > 0 && (
          <ProgressBar value={replaceProgress} color="orange" label={replaceProgress === 100 ? "File replaced successfully!" : "Uploading..."} />
        )}
        <div className="fam-footer">
          <button className="fam-btn" onClick={closeReplaceModal} disabled={replaceFileUploading}>Cancel</button>
          <button className="fam-btn fam-btn--danger" onClick={handleReuploadSpss} disabled={!selectedReplaceFile || replaceFileUploading}>
            <IconReplace /> {replaceFileUploading ? "Replacing…" : "Confirm replace"}
          </button>
        </div>
      </FileActionModal>

      {/* MODAL 2 — Re-upload SPSS */}
      <FileActionModal
        isOpen={showReuploadModal}
        onClose={closeReuploadModal}
        type="reupload"
        title="Re-upload SPSS data"
        icon={<IconDatabase />}
        iconClass="purple"
      >
        <p className="fam-desc">Choose how to update the existing project data with your new SPSS file.</p>
        <div className="fam-radio-group">
          <label
            className={`fam-radio-card${reuploadMode_type === "truncate" ? " fam-radio-card--active" : ""}`}
            onClick={() => setReuploadModeType("truncate")}
          >
            <input type="radio" name="reupload_mode" value="truncate" checked={reuploadMode_type === "truncate"} onChange={() => setReuploadModeType("truncate")} />
            <div>
              <div className="fam-radio-label">Truncate</div>
              <div className="fam-radio-desc">Replace all existing rows with new data</div>
            </div>
          </label>
          <label
            className={`fam-radio-card${reuploadMode_type === "append" ? " fam-radio-card--active" : ""}`}
            onClick={() => setReuploadModeType("append")}
          >
            <input type="radio" name="reupload_mode" value="append" checked={reuploadMode_type === "append"} onChange={() => setReuploadModeType("append")} />
            <div>
              <div className="fam-radio-label">Append</div>
              <div className="fam-radio-desc">Add new rows to existing data</div>
            </div>
          </label>
        </div>
        <DropZone accept=".sav,.SAV" onFileSelect={setReuploadFile} selectedFile={reuploadFile} hint=".sav / .SAV files only" />
        {reuploadProgress > 0 && (
          <ProgressBar value={reuploadProgress} color="purple" label={reuploadProgress === 100 ? "Upload complete!" : "Uploading…"} />
        )}
        <div className="fam-footer">
          <button className="fam-btn" onClick={closeReuploadModal} disabled={reuploadLoading}>Cancel</button>
          <button className="fam-btn fam-btn--primary" onClick={handleReuploadSpss} disabled={!reuploadFile || reuploadLoading}>
            <IconUpload /> {reuploadLoading ? "Uploading…" : reuploadMode_type === "truncate" ? "Confirm replace" : "Confirm append"}
          </button>
        </div>
      </FileActionModal>

      {/* MODAL 3 — Upload QNR */}
      <FileActionModal
        isOpen={showQnrModal}
        onClose={closeQnrModal}
        type="qnr"
        title={qnrFilename ? "Replace questionnaire file" : "Upload questionnaire file"}
        icon={<IconFile />}
        iconClass="green"
      >
        <p className="fam-desc">Upload your questionnaire document to enable data validation against SPSS variables.</p>
        <div className="fam-alert fam-alert--info">
          <span className="fam-alert-icon"><IconInfo /></span>
          <span>Accepted formats: Word (.doc, .docx), Excel (.xls, .xlsx), and PDF (.pdf)</span>
        </div>
        <div className="fam-pills">
          {[".doc", ".docx", ".xls", ".xlsx", ".pdf"].map((ext) => (
            <span key={ext} className="fam-pill">{ext}</span>
          ))}
        </div>
        <DropZone accept=".doc,.docx,.xls,.xlsx,.pdf" onFileSelect={setSelectedQnrFile} selectedFile={selectedQnrFile} hint="Word, Excel, or PDF" />
        {qnrProgress > 0 && (
          <ProgressBar value={qnrProgress} color="green" label={qnrProgress === 100 ? "QNR uploaded successfully!" : "Uploading…"} />
        )}
        <div className="fam-footer">
          <button className="fam-btn" onClick={closeQnrModal} disabled={qnrUploading}>Cancel</button>
          <button className="fam-btn fam-btn--success" onClick={handleUploadQnr} disabled={!selectedQnrFile || qnrUploading}>
            <IconCheck /> {qnrUploading ? "Uploading…" : "Upload QNR"}
          </button>
        </div>
      </FileActionModal>

    </div>
  );
};

export default ProjectDetails;